<script lang="ts">
  import { onDestroy, tick } from 'svelte';
  import DieIcon from './die-icon.svelte';
  import Dietip from './die-tip.svelte';
  import DieTicks from './die-ticks.svelte';
  import { DieFace } from '../models/face.type';
  import { Die as DieType } from '../models/die.type';
  import { peekedDie } from '../../store';

  interface Props {
    class?: string;
    die: DieType;
    /**
     * Makes the die a button of its own. Leave it out when something around the
     * die already takes the click, such as a <Draggable>, so that there is one
     * button to tab to rather than one inside another.
     */
    onClick?: (die: DieType) => void;
    die3D?: boolean;
    hover?: boolean;
    showInfo?: boolean;
    allDice?: Array<DieType>;
    sizeMod?: number;
    /**
     * Redraw when this changes. A tray passes its dice list: a die changed in
     * place is still the same object, so the tray's new list is the only sign
     * that something about it may be different.
     */
    redraw?: unknown;
  }

  let {
    class: clazz = '',
    die,
    onClick = undefined,
    die3D = true,
    hover = true,
    showInfo = false,
    allDice = [],
    sizeMod = 0,
    redraw = undefined,
  }: Props = $props();

  let element: HTMLElement | undefined = $state();
  /** Staggers the bounce, so a tray of dice does not land in lockstep. */
  let animationDelay = Math.random() * 0.2;

  /*
    roll() changes the die in place, and the same object again is no change as
    far as runes are concerned -- `die = die` did this job in legacy mode and
    does nothing now. `view` is a fresh copy of the die, prototype and all,
    whenever it is rolled, a new one is passed in or `redraw` changes, and it
    is what the markup and the tip read.
  */
  let rolls = $state(0);
  let view: DieType = $derived.by(() => {
    void rolls;
    void redraw;
    return Object.assign(Object.create(Object.getPrototypeOf(die)), die);
  });

  let rolling = false;

  /*
    The board holds these components by bind:this and asks them two things.
    It used to read the `die` and `state` props straight off the instance,
    through accessors, which runes mode does not have.
  */
  export function getDie() {
    return die;
  }
  export function isRolling() {
    return rolling;
  }

  function clickEventLocal() {
    if (onClick && !rolling) {
      onClick(die);
    }
  }

  /*
    The 3D faces, as [class, index into die.faces]. The cube is turned to show
    whichever one is active; FACE_ROTATION holds the quarter turns about X and Y
    that bring each face to the front.
  */
  const FACES: Array<[string, number]> = [
    ['front', 5],
    ['back', 4],
    ['left', 3],
    ['right', 2],
    ['top', 1],
    ['bottom', 0],
  ];
  const FACE_ROTATION: Array<[number, number]> = [
    [1, 0],
    [3, 0],
    [0, 3],
    [0, 1],
    [0, 2],
    [0, 0],
  ];

  let rotX = $state(0);
  let rotY = $state(0);
  /** Whole turns added on top, stepped each roll so the cube always spins. */
  let count = Math.round(Math.random()) * 2 - 1;
  setRotation();

  /*
    The tip opens on a dwell, and both input types measure the same one. A
    pointer starts it by arriving; a finger starts it by staying still, which
    <Draggable> reports as a hold. Keep going and the tip grows its hints, so a
    player who lingers finds them without being told they exist.
  */

  /** Pointer resting on the die before the tip opens. */
  const HOVER_DELAY = 300;
  /** Tip open before its hints unfold. */
  const HINT_DELAY = 1000;

  let hoverTimer: ReturnType<typeof setTimeout> | null = null;
  let hintTimer: ReturnType<typeof setTimeout> | null = null;
  let dwelt = $state(false);
  let showHints = $state(false);

  let peeked = $derived($peekedDie === die);
  let tipOpen = $derived(hover && (dwelt || peeked));
  $effect.pre(() => armHints(tipOpen));

  function armHints(open: boolean) {
    if (hintTimer !== null) {
      clearTimeout(hintTimer);
      hintTimer = null;
    }
    if (!open) {
      showHints = false;
      return;
    }
    hintTimer = setTimeout(() => {
      hintTimer = null;
      showHints = true;
    }, HINT_DELAY);
  }

  function startDwell() {
    if (!hover) return;
    if (hoverTimer !== null) clearTimeout(hoverTimer);
    hoverTimer = setTimeout(() => {
      hoverTimer = null;
      dwelt = true;
    }, HOVER_DELAY);
  }

  function endDwell() {
    if (hoverTimer !== null) {
      clearTimeout(hoverTimer);
      hoverTimer = null;
    }
    dwelt = false;
  }

  /*
    A press is the start of a drag, so the tip goes away: it would otherwise sit
    over the slots the die is heading for. Releasing without going anywhere
    starts the dwell over -- and if the press did turn into a drag, the pointer
    left the die long ago and mouseleave has already dealt with it.
  */
  function onPress() {
    endDwell();
  }

  onDestroy(() => {
    endDwell();
    armHints(false);
    // A board is torn down with a finger still on it if the battle ends mid
    // peek; nothing else would ever close this one.
    if ($peekedDie === die) {
      peekedDie.set(null);
    }
  });

  /*
    The bounce lives only as long as it plays. A drag ghost is a copy of this
    element's markup, and a class left behind would start the bounce over again
    on every copy -- once when the drag begins, and again at the drop.
  */
  let bouncing = $state(false);

  async function bounce() {
    // Off, a reflow, then on again, so a reroll mid-bounce starts it afresh.
    bouncing = false;
    await tick();
    element?.offsetWidth;
    bouncing = true;
  }

  function onAnimationEnd(event: AnimationEvent) {
    // The level ticks animate too, and their events bubble up to here.
    if (event.target === element) bouncing = false;
  }

  /** Resolves false if the die landed on a broken face. */
  export async function roll(): Promise<boolean> {
    rolling = true;
    die.roll();
    rolls++;
    setRotation();
    bounce();

    await new Promise((resolve) => setTimeout(resolve, 900));
    rolling = false;
    return die.value()[0] !== DieFace.Broken;
  }

  function setRotation() {
    const [x, y] = FACE_ROTATION[die.activeFace] ?? [0, 0];
    rotX = x * 90 + 360 * count;
    rotY = y * 90 + 360 * count;
    count += Math.round(Math.random()) * 2 - 1;
  }
</script>

<div style="position:relative">
  <!-- The role and tabindex are set together, when there is an onClick. -->
  <!-- svelte-ignore a11y_no_noninteractive_tabindex a11y_no_static_element_interactions -->
  <div
    class="
    die
    die-{view.index}
    die-level-{view.level}
    {view.colour}
    {view.selected ? 'selected' : ''}
    {view.used ? 'used' : ''}
    {clazz || ''}
    {view.material || ''}
    size-{sizeMod}
    {bouncing ? 'rolling' : ''}
  "
    style="animation-delay: {animationDelay}s"
    role={onClick ? 'button' : undefined}
    tabindex={onClick ? 0 : undefined}
    onclick={clickEventLocal}
    onkeydown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') clickEventLocal();
    }}
    onmouseenter={startDwell}
    onmouseleave={endDwell}
    onmousedown={onPress}
    onmouseup={startDwell}
    onanimationend={onAnimationEnd}
    bind:this={element}
  >
    {#if die3D}
      <div class="die-3d" style="transform: rotateX({rotX}deg) rotateY({rotY}deg)">
        {#each FACES as [side, index]}
          <div class="face {side}">
            <DieTicks level={view.level}></DieTicks>
            <DieIcon face={view.faces[index]} {sizeMod} />
          </div>
        {/each}
      </div>
    {:else}
      <div class="die-2d face">
        <DieTicks level={view.level}></DieTicks>
        <DieIcon face={view.value()} {sizeMod} />
      </div>
    {/if}
  </div>

  <Dietip
    die={view}
    {allDice}
    open={tipOpen}
    {showInfo}
    hintsOpen={showHints}
    above={peeked}
    anchor={element}
  />
</div>

<style lang="less">
  .die {
    width: var(--dice-size);
    height: var(--dice-size);
    // Holding a die opens its tip, so the platform must not take the press for
    // its own callout or selection instead.
    -webkit-touch-callout: none;
    user-select: none;
    transition:
      opacity 0.3s,
      filter 0.3s;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    animation-duration: 1000ms;
    // perspective: 1000px;
    // perspective-origin: 50% 100%;

    &.size-2 {
      width: calc(var(--dice-size) / 2);
      height: calc(var(--dice-size) / 2);
    }
    &.size-1 {
      width: calc(var(--dice-size) / 1.5);
      height: calc(var(--dice-size) / 1.5);
    }

    &.rolling {
      animation-name: bounce;
    }
  }

  .die-2d {
    min-width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    position: relative;
  }

  .die-3d {
    width: var(--dice-size);
    height: var(--dice-size);
    position: absolute;
    transform-style: preserve-3d;
    transition: transform 900ms;
    will-change: transform;

    .face {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      width: var(--dice-size);
      height: var(--dice-size);
      border-radius: 4px;
      margin: 0 auto;
    }

    .front {
      transform: translateZ(calc(var(--dice-size) / 2));
    }

    .back {
      transform: rotateX(-180deg) rotateZ(180deg) translateZ(calc(var(--dice-size) / 2));
    }

    .right {
      transform: rotateY(90deg) translateZ(calc(var(--dice-size) / 2));
    }

    .left {
      transform: rotateY(-90deg) translateZ(calc(var(--dice-size) / 2));
    }

    .top {
      transform: rotateX(90deg) translateZ(calc(var(--dice-size) / 2));
    }

    .bottom {
      transform: rotateX(-90deg) translateZ(calc(var(--dice-size) / 2));
    }
  }

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      -webkit-transform: scale(1);
    }
    15% {
      -webkit-transform: scale(1.3);
    }
    40% {
      -webkit-transform: scale(1.2);
    }
    60% {
      -webkit-transform: scale(1.1);
    }
  }
</style>
